<template>
  <!-- Desktop Layout -->
  <div class="p-4 md:p-6">
    <!-- Top Header Bar -->
    <div class="mb-6">
      <div class="rounded-lg p-4 flex justify-between items-center">
        <div class="brand-font text-3xl">Travley</div>

        <!-- Desktop Search Bar -->
        <div class="flex-1 max-w-md mx-8">
          <div @click="openSearchAssistant" class="bg-gray-100 rounded-2xl px-4 py-3 cursor-pointer transition-all duration-200 hover:bg-gray-200 flex items-center">
            <svg class="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span class="text-gray-500 flex-1">Ask Travley anything about travel...</span>
            <div class="flex items-center text-xs text-gray-400">
              <span class="bg-gray-300 px-2 py-1 rounded">{{ dailySearchCount }}/3</span>
            </div>
          </div>
        </div>

        <div class="flex items-center space-x-4">
          <span class="text-lg text-gray-800 font-semibold">{{ user.name || 'User' }}</span>
          <div class="w-10 h-10 rounded-full overflow-hidden">
            <img
              :src="user.picture || 'https://via.placeholder.com/40'"
              alt="Profile"
              class="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Desktop Grid Layout -->
    <div class="grid grid-cols-12 gap-4">
      <!-- Left Column -->
      <div class="col-span-3 space-y-4">
        <!-- Time and Date Tile -->
        <div class="bg-white rounded-3xl p-6 h-48 flex flex-col justify-center items-center">
          <div class="text-center">
            <div class="text-3xl font-bold text-gray-800 mb-4">{{ currentTime }}</div>
            <div class="text-xl font-semibold text-gray-600 mb-1">{{ currentDate }}</div>
            <div class="text-lg text-gray-500">{{ currentDay }}, {{ currentMonth }}</div>
          </div>
        </div>
        
        <!-- Train Tile -->
        <div
          @click="handleTrainClick"
          class="rounded-3xl p-5 h-40 cursor-pointer hover:shadow-md transition-all flex items-center justify-between"
          style="background-color: #FB9C80;"
        >
          <div class="flex-1">
            <h3 class="text-3xl font-bold text-white mb-3">Train</h3>
            <p class="text-lg font-medium text-white mb-6">Find train timings</p>
          </div>
          <div class="flex-shrink-0">
            <img src="/train.svg" alt="Train" class="h-8 w-8" />
          </div>
        </div>

        <!-- Metro Tile -->
          <div @click="handleMetroClick" class="rounded-3xl p-5 h-36 cursor-pointer hover:shadow-md transition-all flex items-center justify-between" style="background-color: #ABBCF4;">
            <div class="flex-1">
              <h3 class="text-3xl font-bold text-white mb-3">Metro</h3>
              <p class="text-lg font-medium text-white mb-6">Find metro routes</p>
            </div>
            <div class="flex-shrink-0">
              <img src="/metro.svg" alt="Metro" class="h-8 w-8" />
            </div>
          </div>
      </div>

      <!-- Center Column - Medium Tiles -->
      <div class="col-span-5 space-y-4">
        <!-- Top Center Tiles Row -->
        <div class="grid grid-cols-2 gap-4">
          <!-- Weather Tile -->
          <div @click="handleWeatherClick" class="bg-white rounded-3xl p-6 h-48 cursor-pointer hover:shadow-md transition-all" style="background-color: #a6d6ff;">
            <div class="flex justify-between items-start mb-4">
              <h3 class="text-3xl font-bold text-white">Weather</h3>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
              </svg>
            </div>

            <!-- Loading state -->
            <div v-if="weatherLoading" class="text-center">
              <div class="animate-pulse">
                <div class="text-sm text-white">Loading weather...</div>
              </div>
            </div>

            <!-- Weather data -->
            <div
              v-else-if="currentWeather && currentWeather.main && currentWeather.weather"
              class="space-y-2"
            >
              <div class="flex items-center justify-between">
                <div class="text-2xl font-bold text-white">{{ Math.round(currentWeather.main.temp) }}°C</div>
                <img
                  :src="`https://openweathermap.org/img/wn/${currentWeather.weather[0].icon}.png`"
                  :alt="currentWeather.weather[0].description"
                  class="w-12 h-12"
                />
              </div>
              <div class="text-sm text-white capitalize">{{ currentWeather.weather[0].description }}</div>
              <div class="text-xs text-white">{{ weatherLocation || 'Current Location' }}</div>
            </div>

            <!-- Error state -->
            <div v-else-if="weatherError" class="text-center">
              <div class="text-xs text-white mt-1">Tap to retry</div>
            </div>

            <!-- Default state -->
            <div v-else class="text-center">
              <div class="text-sm text-white">Tap for weather</div>
            </div>
          </div>
          
          <!-- Flights Tile -->
          <div @click="handleFlightClick" class="rounded-3xl p-6 h-48 text-center  cursor-pointer hover:shadow-md transition-all" style="background-color: #ABBCF4;">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            <h2 class="text-3xl font-bold text-white">Flights</h2>
            <p class="text-white text-lg font-medium p-4 mb-6">Find flight pricing</p>
          </div>
        </div>
        
        <!-- Task Management Tile -->
        <div @click="handleTaskClick" class="bg-white rounded-3xl p-6 h-[19.8rem] cursor-pointer hover:shadow-md transition-all">
          <div class="flex justify-between items-start mb-4">
            <h3 class="text-2xl font-bold text-gray-800">Tasks</h3>
            <div class="flex items-center space-x-2">
              <button @click.stop="refreshTasksData"
                      class="p-1 rounded-full transition-colors hover:bg-purple-100 text-purple-500"
                      title="Refresh tasks">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
              </button>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
              </svg>
            </div>
          </div>

          <!-- Task Summary -->
          <div v-if="taskSummary" class="space-y-2">
            <div class="flex justify-between items-center">
              <span class="text-xl text-gray-600">Total Tasks</span>
              <span class="text-xl font-semibold text-gray-800">{{ taskSummary.total }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-xl text-gray-600">Pending</span>
              <span class="text-xl font-semibold text-orange-600">{{ taskSummary.pending }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-xl text-gray-600">Scheduled</span>
              <span class="text-xl font-semibold text-purple-600">{{ taskSummary.scheduled }}</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2 mt-3">
              <div
                class="bg-purple-500 h-2 rounded-full transition-all duration-300"
                :style="{ width: `${taskSummary.completionPercentage}%` }"
              ></div>
            </div>
            <div class="text-xl text-gray-500 text-center">{{ taskSummary.completionPercentage }}% organized</div>
          </div>

          <!-- Loading state -->
          <div v-else-if="taskLoading" class="flex items-center justify-center h-20">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
          </div>

          <!-- Default state -->
          <div v-else class="text-center py-4">
            <p class="text-gray-500 text-sm">Click to manage your tasks</p>
          </div>
        </div>
      </div>

      <!-- Right Column - Dynamic Content -->
      <div class="col-span-4">
        <div class="rounded-3xl h-full min-h-[500px] relative">

          <!-- Default State -->
          <div v-if="!activeContent" class="flex flex-col gap-4 h-full">
            <!-- Top Large Tile -->
            <div class="h-48">
              <PlannedTripsComponent />
            </div>

            <!-- Middle Row -->
            <div class="flex gap-4 h-48">
              <!-- Hotspots Carousel -->
              <div class="w-48 h-48">
                <HotspotsCarousel @hotspot-selected="handleHotspotSelected" />
              </div>

              <!-- Right Column -->
              <div class="flex flex-col flex-1"  style="height: 214px">
                <!-- Hotel Tile -->
                <div @click="handleHotelClick" class="rounded-3xl h-16 mb-4 flex items-center justify-between px-4 cursor-pointer hover:shadow-md transition-all"
                  style="background-color: #ABBCF4;"
                >
                  <h3 class="text-lg font-bold text-white">Stay</h3>
                  <img src="/hotel.svg" alt="Hotel" class="h-8 w-8" />
                </div>

                <!-- Restaurant Tile -->
                <div @click="handleRestaurantClick" class="rounded-3xl h-16 mb-4 flex items-center justify-between px-4 cursor-pointer hover:shadow-md transition-all"
                  style="background-color: #69CBD8;"
                >
                  <h3 class="text-lg font-bold text-white">Dine</h3>
                  <img src="/restuarant.svg" alt="Restaurant" class="h-8 w-8" />
                </div>

                <!-- Shopping Tile -->
                <div @click="handleShoppingClick" class="rounded-3xl h-16 flex items-center justify-between px-4 cursor-pointer hover:shadow-md transition-all"
                  style="background-color: #FB9C80;"
                >
                  <h3 class="text-lg font-bold text-white">Shop</h3>
                  <img src="/shopping.svg" alt="Shopping" class="h-8 w-8" />
                </div>
              </div>
            </div>

            <!-- Forecast Tile -->
            <div class="rounded-3xl h-20 text-center cursor-pointer hover:shadow-md transition-all mt-6" style="background-color: #ABBCF4;">
              <div class="flex items-center justify-between h-full px-4">
                <div class="flex flex-col items-start">
                  <h2 class="text-lg font-bold text-white">Forecast</h2>
                  <p class="text-white text-xs font-medium">5-Day Trend</p>
                </div>

                <!-- Temperature Graph - No padding constraints -->
                <div class="flex-1 h-8 relative">
                  <svg class="w-full h-full" viewBox="0 0 120 32" preserveAspectRatio="none">
                    <defs>
                      <!-- Gradient for the curve -->
                      <linearGradient id="tempGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style="stop-color:rgba(255,255,255,0.6);stop-opacity:1" />
                        <stop offset="100%" style="stop-color:rgba(255,255,255,0.1);stop-opacity:1" />
                      </linearGradient>
                    </defs>

                    <!-- Gradient fill under curve -->
                    <path
                      :d="temperatureFillPath"
                      fill="url(#tempGradient)"
                      opacity="0.4"
                    />

                    <!-- Smooth curved line -->
                    <path
                      :d="temperatureCurvePath"
                      fill="none"
                      stroke="white"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />

                    <!-- Temperature dots -->
                    <circle
                      v-for="(point, pointIndex) in temperatureDots"
                      :key="pointIndex"
                      :cx="point.x"
                      :cy="point.y"
                      r="2"
                      fill="white"
                    />
                  </svg>
                </div>

                <div class="text-right">
                  <span class="text-lg font-bold text-white">{{ currentTemp }}°</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Close Button -->
          <button v-if="activeContent" @click="closeContent" class="absolute top-1 right-1 z-10 text-gray-500 hover:text-black transition-colors p-2">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>

          <!-- Dynamic Content -->
          <div v-if="activeContent" class="h-full overflow-y-auto">
            <!-- Hotels Content -->
            <div v-if="activeContent === 'hotels'" class="h-full">
              <HotelsInline :hotels-data="hotelsData" :is-loading="hotelsLoading" :error="hotelsError" @retry="fetchHotels"/>
            </div>

            <!-- Restaurants Content -->
            <div v-if="activeContent === 'restaurants'" class="h-full">
              <RestaurantsInline :restaurants-data="restaurantsData" :is-loading="restaurantsLoading" :error="restaurantsError" @retry="fetchRestaurants"/>
            </div>

            <!-- Shopping Content -->
            <div v-if="activeContent === 'shopping'" class="h-full">
              <ShoppingInline :shopping-data="shoppingData" :is-loading="shoppingLoading" :error="shoppingError" @retry="fetchShopping"/>
            </div>

            <!-- Weather Content -->
            <div v-if="activeContent === 'weather'" class="h-full">
              <WeatherPopup :isInline="true" :isVisible="true" class="h-full"/>
            </div>

            <!-- Train Content -->
            <div v-if="activeContent === 'train'" class="h-full">
              <TrainSearchPopup :isInline="true" :isVisible="true" class="h-full"/>
            </div>

            <!-- Metro Content -->
            <div v-if="activeContent === 'metro'" class="h-full">
              <MetroSearchPopup :isInline="true" :isVisible="true" class="h-full"/>
            </div>

            <!-- Task Management Content -->
            <div v-if="activeContent === 'tasks'" class="h-full">
              <div class="bg-white rounded-3xl h-full p-4">
                <TaskManagementEnhanced @task-created="onTaskCreated" @task-deleted="onTaskDeleted" />
              </div>
            </div>

            <!-- Flights Content -->
            <div v-if="activeContent === 'flights'" class="h-full">
              <div class="h-full bg-[#ABBCF4] rounded-3xl p-6 flex flex-col items-center justify-center">
                <div class="text-center">
                  <img src="/plane.png" alt="Plane" class="h-20 w-20 mx-auto mb-6 opacity-80" />
                  <h2 class="text-3xl font-bold text-white mb-4">Flight Search</h2>
                  <p class="text-lg font-medium text-white mb-6">Get the best pricing for your next adventure!</p>
                  <button
                    @click="showComingSoonModal = true"
                    class="w-full bg-blue-500 text-white px-12 py-3 rounded-3xl hover:bg-blue-600 transition-colors font-medium"
                  >
                    Search Flights
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- Coming Soon Modal -->
    <ComingSoonModal :isVisible="showComingSoonModal" @close="showComingSoonModal = false"/>

    <!-- Desktop Search Assistant Popup -->
    <div v-if="showSearchAssistant" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div class="w-[600px] h-[600px] rounded-3xl overflow-hidden bg-[#ABBCF4]">
        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 bg-[#ABBCF4] sticky top-0 z-20 rounded-t-3xl">
          <button @click="closeSearchAssistant" class="text-white p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
            </svg>
          </button>
          <h2 class="text-xl font-bold text-white">Travley Assistant</h2>
          <div class="flex items-center text-white text-sm">
            <span class="bg-white bg-opacity-20 px-2 py-1 rounded">{{ dailySearchCount }}/3</span>
          </div>
        </div>

        <!-- Chat Container -->
        <div class="flex flex-col" style="height: calc(600px - 80px);">
          <!-- Messages Area -->
          <div class="flex-1 overflow-y-auto p-6 space-y-4">
            <!-- Welcome Message -->
            <div v-if="chatMessages.length === 0" class="text-center py-6">
              <div class="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 class="text-white text-lg font-semibold mb-2">Hi! I'm Travley</h3>
              <p class="text-white text-opacity-80 text-sm mb-4">Ask me anything about travel, destinations, or planning your trip!</p>

              <!-- Example questions -->
              <div class="space-y-2 text-left max-w-sm mx-auto">
                <p class="text-white text-opacity-60 text-xs mb-2">Try asking:</p>
                <button @click="searchQuery = 'Tell me about Paris'; sendMessage()" class="w-full text-left bg-white bg-opacity-10 rounded-lg p-2 text-white text-sm hover:bg-opacity-20 transition-all">
                  "Tell me about Paris"
                </button>
                <button @click="searchQuery = 'Best time to visit Japan'; sendMessage()" class="w-full text-left bg-white bg-opacity-10 rounded-lg p-2 text-white text-sm hover:bg-opacity-20 transition-all">
                  "Best time to visit Japan"
                </button>
                <button @click="searchQuery = 'Travel tips for Europe'; sendMessage()" class="w-full text-left bg-white bg-opacity-10 rounded-lg p-2 text-white text-sm hover:bg-opacity-20 transition-all">
                  "Travel tips for Europe"
                </button>
              </div>
            </div>

            <!-- Chat Messages -->
            <div v-for="(message, index) in chatMessages" :key="index" class="flex" :class="message.type === 'user' ? 'justify-end' : 'justify-start'">
              <div class="max-w-[80%] rounded-2xl p-3" :class="message.type === 'user' ? 'bg-white text-gray-800' : 'bg-white bg-opacity-20 text-white'">
                <p class="text-sm">{{ message.content }}</p>
                <span class="text-xs opacity-70 mt-1 block">{{ message.timestamp }}</span>
              </div>
            </div>

            <!-- Loading Message -->
            <div v-if="isSearching" class="flex justify-start">
              <div class="bg-white bg-opacity-20 text-white rounded-2xl p-3">
                <div class="flex items-center space-x-2">
                  <div class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span class="text-sm">Travley is thinking...</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Input Area - Fixed at bottom -->
          <div class="flex-shrink-0 p-4 bg-white bg-opacity-10">
            <div class="flex items-center space-x-3">
              <div class="flex-1 relative">
                <input
                  v-model="searchQuery"
                  @keyup.enter="sendMessage"
                  :disabled="dailySearchCount >= 3 || isSearching"
                  placeholder="Type your travel question here..."
                  class="w-full bg-white bg-opacity-20 text-white placeholder-white placeholder-opacity-70 rounded-xl px-4 py-3 pr-12 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 focus:bg-opacity-30 transition-all"
                />
                <div class="absolute right-3 top-1/2 transform -translate-y-1/2 text-white text-opacity-50">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
              </div>
              <button
                @click="sendMessage"
                :disabled="!searchQuery.trim() || dailySearchCount >= 3 || isSearching"
                class="bg-white text-[#ABBCF4] rounded-xl px-6 py-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-opacity-90 flex items-center space-x-2"
              >
                <span>Send</span>
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            <p v-if="dailySearchCount >= 3" class="text-white text-opacity-70 text-xs mt-2 text-center">
              Daily search limit reached. Try again tomorrow!
            </p>
            <p v-else class="text-white text-opacity-50 text-xs mt-2 text-center">
              {{ 3 - dailySearchCount }} questions remaining today
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TaskManagementEnhanced from '@/components/TaskManagementEnhanced.vue';
import HotelsInline from '@/components/HotelsInline.vue';
import RestaurantsInline from '@/components/RestaurantsInline.vue';
import ShoppingInline from '@/components/ShoppingInline.vue';
import WeatherPopup from '@/components/WeatherPopup.vue';
import TrainSearchPopup from '@/components/TrainSearchPopup.vue';
import MetroSearchPopup from '@/components/MetroSearchPopup.vue';
import PlannedTripsComponent from '@/components/PlannedTripsComponent.vue';
import HotspotsCarousel from '@/components/HotspotsCarousel.vue';
import ComingSoonModal from '@/components/ComingSoonModal.vue';
import { getNearbyPlaces } from '@/services/hotelsService';
import { searchRestaurants, searchShopping } from '@/services/searchService';
import { getCurrentLocation } from '@/services/weatherService';
import { getTasks } from '@/services/taskService';
import { isAuthenticated } from '@/services/authService';
import { base, weatherAPIKey, agent } from '@/config/api';

export default {
  name: 'TestDesktopView',
  components: {
    TaskManagementEnhanced,
    HotelsInline,
    RestaurantsInline,
    ShoppingInline,
    WeatherPopup,
    TrainSearchPopup,
    MetroSearchPopup,
    PlannedTripsComponent,
    HotspotsCarousel,
    ComingSoonModal
  },
  props: {
    user: {
      type: Object,
      required: true
    },
    currentTime: {
      type: String,
      required: true
    },
    currentDate: {
      type: String,
      required: true
    },
    currentDay: {
      type: String,
      required: true
    },
    currentMonth: {
      type: String,
      required: true
    },

  },
  data() {
    return {
      activeContent: null,
      // Hotels data
      hotelsData: null,
      hotelsLoading: false,
      hotelsError: null,
      // Restaurants data
      restaurantsData: null,
      restaurantsLoading: false,
      restaurantsError: null,
      // Shopping data
      shoppingData: null,
      shoppingLoading: false,
      shoppingError: null,
      // Weather data
      currentWeather: null,
      weatherLoading: false,
      weatherError: null,
      weatherLocation: null,
      // Task data
      taskSummary: null,
      taskLoading: false,
      taskError: null,
      // Coming Soon Modal
      showComingSoonModal: false,

      // Temperature graph data
      forecastData: [],

      // Search Assistant
      showSearchAssistant: false,
      searchQuery: '',
      chatMessages: [],
      isSearching: false,
      dailySearchCount: 0

    };
  },
  computed: {
    temperatureDots() {
      if (!this.forecastData.length) return [];

      const width = 120;
      const height = 32;
      const padding = 10;

      const temps = this.forecastData.map(day => day.temp);
      const minTemp = Math.min(...temps);
      const maxTemp = Math.max(...temps);
      const tempRange = maxTemp - minTemp || 1;

      return this.forecastData.map((day, index) => {
        const x = padding + (index * (width - 2 * padding)) / (this.forecastData.length - 1);
        const y = padding + ((maxTemp - day.temp) / tempRange) * (height - 2 * padding);
        return { x, y };
      });
    },

    temperatureCurvePath() {
      if (!this.temperatureDots.length) return '';

      if (this.temperatureDots.length === 1) {
        const point = this.temperatureDots[0];
        return `M ${point.x} ${point.y}`;
      }

      let path = `M ${this.temperatureDots[0].x} ${this.temperatureDots[0].y}`;

      for (let i = 1; i < this.temperatureDots.length; i++) {
        const current = this.temperatureDots[i];
        const previous = this.temperatureDots[i - 1];

        // Create smooth curves using quadratic bezier curves
        const controlX = (previous.x + current.x) / 2;

        if (i === 1) {
          path += ` Q ${controlX} ${previous.y} ${current.x} ${current.y}`;
        } else {
          path += ` T ${current.x} ${current.y}`;
        }
      }

      return path;
    },

    temperatureFillPath() {
      if (!this.temperatureDots.length) return '';

      const height = 32;
      const padding = 12;
      const bottomY = height - padding;

      let path = this.temperatureCurvePath;

      if (path) {
        // Add the bottom part to create a filled area
        const lastPoint = this.temperatureDots[this.temperatureDots.length - 1];
        const firstPoint = this.temperatureDots[0];

        path += ` L ${lastPoint.x} ${bottomY}`;
        path += ` L ${firstPoint.x} ${bottomY}`;
        path += ' Z';
      }

      return path;
    },

    currentTemp() {
      if (this.currentWeather && this.currentWeather.main) {
        return Math.round(this.currentWeather.main.temp);
      }
      return '--';
    }
  },
  methods: {
    handleWeatherClick() {
      this.activeContent = 'weather';
      this.fetchWeather();
      this.$emit('weather-clicked');
    },
    handleTrainClick() {
      this.activeContent = 'train';
      this.$emit('train-clicked');
    },
    handleMetroClick() {
      this.activeContent = 'metro';
      this.$emit('metro-clicked');
    },

    // New methods for hotels, restaurants, and shopping
    handleHotelClick() {
      this.activeContent = 'hotels';
      this.fetchHotels();
    },

    handleRestaurantClick() {
      this.activeContent = 'restaurants';
      this.fetchRestaurants();
    },

    handleShoppingClick() {
      this.activeContent = 'shopping';
      this.fetchShopping();
    },

    handleFlightClick() {
      this.activeContent = 'flights';
      this.$emit('flight-clicked');
    },

    handleTaskClick() {
      this.activeContent = 'tasks';
      this.$emit('task-clicked');
    },

    handleHotspotSelected(hotspot) {
      // Handle hotspot selection - could open explore page or show details
      console.log('Hotspot selected:', hotspot);
      this.$emit('hotspot-selected', hotspot);
    },

    closeContent() {
      this.activeContent = null;
      // Clear data when closing
      this.hotelsData = null;
      this.restaurantsData = null;
      this.shoppingData = null;
      this.currentWeather = null;
      // Note: We don't clear taskSummary as it should persist
    },

    async fetchHotels() {
      this.hotelsLoading = true;
      this.hotelsError = null;

      try {
        const data = await getNearbyPlaces();
        this.hotelsData = data;
      } catch (error) {
        this.hotelsError = error.message || 'Failed to fetch nearby hotels. Please try again.';
      } finally {
        this.hotelsLoading = false;
      }
    },

    async fetchRestaurants() {
      this.restaurantsLoading = true;
      this.restaurantsError = null;

      try {
        const location = await getCurrentLocation();
        const data = await searchRestaurants(location.lat, location.lon);
        this.restaurantsData = data;
      } catch (error) {
        this.restaurantsError = error.message || 'Failed to fetch nearby restaurants. Please try again.';
      } finally {
        this.restaurantsLoading = false;
      }
    },

    async fetchShopping() {
      this.shoppingLoading = true;
      this.shoppingError = null;

      try {
        const location = await getCurrentLocation();
        const data = await searchShopping(location.lat, location.lon);
        this.shoppingData = data;
      } catch (error) {
        this.shoppingError = error.message || 'Failed to fetch nearby shopping places. Please try again.';
      } finally {
        this.shoppingLoading = false;
      }
    },

    async fetchWeather() {
      this.weatherLoading = true;
      this.weatherError = null;

      try {
        const location = await getCurrentLocation();
        this.weatherLocation = `${location.city || 'Current Location'}`;

        // Fetch weather data using the same service
        const response = await fetch(
          `${base}/weather?lat=${location.lat}&lon=${location.lon}&appid=${weatherAPIKey}&units=metric`
        );

        if (!response.ok) {
          throw new Error('Weather data unavailable');
        }

        const data = await response.json();
        this.currentWeather = data;
      } catch (error) {
        this.weatherError = error.message || 'Failed to fetch weather data. Please try again.';
      } finally {
        this.weatherLoading = false;
      }
    },

    async fetchTaskSummary() {
      if (!isAuthenticated()) {
        return;
      }

      this.taskLoading = true;
      this.taskError = null;

      try {
        // Check cache first
        const cachedData = this.getTasksFromCache();
        if (cachedData) {
          const tasks = cachedData.tasks;
          const total = tasks.length;
          const scheduled = tasks.filter(task => task.schedule_time).length;
          const pending = total - scheduled;
          const completionPercentage = total > 0 ? Math.round((scheduled / total) * 100) : 0;

          this.taskSummary = {
            total,
            pending,
            scheduled,
            completionPercentage
          };
          this.taskLoading = false;
          return;
        }

        const response = await getTasks(50, 0);

        if (response.success) {
          const tasks = response.tasks;
          const total = tasks.length;
          const scheduled = tasks.filter(task => task.schedule_time).length;
          const pending = total - scheduled;
          const completionPercentage = total > 0 ? Math.round((scheduled / total) * 100) : 0;

          this.taskSummary = {
            total,
            pending,
            scheduled,
            completionPercentage
          };

          // Cache the data
          this.cacheTasksData({
            tasks,
            total,
            timestamp: Date.now()
          });
        }
      } catch (error) {
        console.error('Error fetching task summary:', error);
        this.taskError = error.message || 'Failed to fetch task summary';
      } finally {
        this.taskLoading = false;
      }
    },

    loadForecastData() {
      try {
        // Try to get forecast data from localStorage
        const storedForecast = localStorage.getItem('weatherForecast');
        if (storedForecast) {
          const forecastData = JSON.parse(storedForecast);

          // Extract 5 days of temperature data
          if (forecastData.list && forecastData.list.length > 0) {
            this.forecastData = forecastData.list
              .filter((item, index) => index % 8 === 0) // Get one reading per day (every 8th item for 3-hour intervals)
              .slice(0, 5) // Take only 5 days
              .map(item => ({
                temp: Math.round(item.main.temp),
                date: new Date(item.dt * 1000).toLocaleDateString('en-US', { weekday: 'short' })
              }));
          }
        }

        // If no stored data, create sample data
        if (this.forecastData.length === 0) {
          this.forecastData = this.generateSampleForecast();
        }
      } catch (error) {
        console.error('Error loading forecast data:', error);
        this.forecastData = this.generateSampleForecast();
      }
    },

    generateSampleForecast() {
      // Generate sample temperature data if no real data is available
      const baseTemp = this.currentTemp !== '--' ? parseInt(this.currentTemp) : 25;
      const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

      return days.map((day) => ({
        temp: baseTemp + Math.floor(Math.random() * 10) - 5, // ±5 degrees variation
        date: day
      }));
    },

    // Task cache management methods
    getTasksFromCache() {
      try {
        const cached = localStorage.getItem('tasks_cache');
        if (!cached) return null;

        const data = JSON.parse(cached);
        const now = Date.now();
        const cacheAge = now - data.timestamp;
        const cacheExpiry = 30 * 60 * 1000; // 30 minutes

        if (cacheAge > cacheExpiry) {
          localStorage.removeItem('tasks_cache');
          return null;
        }

        return data;
      } catch (error) {
        console.error('Error reading tasks cache:', error);
        localStorage.removeItem('tasks_cache');
        return null;
      }
    },

    cacheTasksData(data) {
      try {
        localStorage.setItem('tasks_cache', JSON.stringify(data));
      } catch (error) {
        console.error('Error caching tasks data:', error);
      }
    },

    clearTasksCache() {
      localStorage.removeItem('tasks_cache');
    },

    async refreshTasksData() {
      this.clearTasksCache();
      await this.fetchTaskSummary();
    },

    // Event handlers for task operations
    onTaskCreated() {
      // Clear cache and refresh summary when a new task is created
      this.clearTasksCache();
      this.fetchTaskSummary();
    },

    onTaskDeleted() {
      // Clear cache and refresh summary when a task is deleted
      this.clearTasksCache();
      this.fetchTaskSummary();
    },

    // Search Assistant Methods
    openSearchAssistant() {
      this.loadDailySearchCount();
      this.showSearchAssistant = true;
    },

    closeSearchAssistant() {
      this.showSearchAssistant = false;
      this.searchQuery = '';
    },

    loadDailySearchCount() {
      const today = new Date().toDateString();
      const stored = localStorage.getItem('travley_search_count');

      if (stored) {
        const data = JSON.parse(stored);
        if (data.date === today) {
          this.dailySearchCount = data.count;
        } else {
          this.dailySearchCount = 0;
          localStorage.setItem('travley_search_count', JSON.stringify({ date: today, count: 0 }));
        }
      } else {
        this.dailySearchCount = 0;
        localStorage.setItem('travley_search_count', JSON.stringify({ date: today, count: 0 }));
      }
    },

    incrementSearchCount() {
      const today = new Date().toDateString();
      this.dailySearchCount++;
      localStorage.setItem('travley_search_count', JSON.stringify({
        date: today,
        count: this.dailySearchCount
      }));
    },

    async sendMessage() {
      if (!this.searchQuery.trim() || this.dailySearchCount >= 3 || this.isSearching) {
        return;
      }

      const userMessage = {
        type: 'user',
        content: this.searchQuery.trim(),
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      };

      this.chatMessages.push(userMessage);
      const query = this.searchQuery.trim();
      this.searchQuery = '';
      this.isSearching = true;

      try {
        // Call agent worker
        const response = await this.callAgentWorker(query);

        const assistantMessage = {
          type: 'assistant',
          content: response,
          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        };

        this.chatMessages.push(assistantMessage);
        this.incrementSearchCount();
      } catch (error) {
        console.error('Search assistant error:', error);

        const errorMessage = {
          type: 'assistant',
          content: 'Sorry, I encountered an error while processing your request. Please try again later.',
          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        };

        this.chatMessages.push(errorMessage);
      } finally {
        this.isSearching = false;
      }
    },

    async callAgentWorker(query) {
      // Prepare context data
      const context = {
        user: {
          name: this.user?.name || 'Traveler',
          email: this.user?.email || null
        },
        weather: this.currentWeather,
        taskCount: this.taskSummary?.total || 0,
        location: 'User location' // We'll get this from localStorage if available
      };

      try {
        const response = await fetch(`${agent}/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: query,
            context: context
          })
        });

        if (!response.ok) {
          throw new Error('Failed to get response from Travley assistant');
        }

        const data = await response.json();
        return data.response || 'Sorry, I could not process your request at the moment.';
      } catch (error) {
        console.error('Agent worker error:', error);

        // Fallback response for testing
        return this.generateFallbackResponse(query, context);
      }
    },

    generateFallbackResponse(query, context) {
      const userName = context.user?.name || 'Traveler';
      const lowerQuery = query.toLowerCase();

      // Simple pattern matching for common travel queries
      if (lowerQuery.includes('paris')) {
        return `Hi ${userName}! Paris is the capital of France and one of the world's most popular travel destinations. Known for the Eiffel Tower, Louvre Museum, and Notre-Dame Cathedral, Paris offers incredible art, cuisine, and culture. The best time to visit is during spring (April-June) or fall (September-November) when the weather is pleasant and crowds are smaller. Use Travley's advanced maps to explore Paris and find the best nearby restaurants and attractions!`;
      } else if (lowerQuery.includes('japan')) {
        return `Hello ${userName}! Japan is a fascinating destination that blends ancient traditions with modern innovation. The best time to visit is during spring (March-May) for cherry blossoms or fall (September-November) for beautiful autumn colors. Don't miss Tokyo, Kyoto, and Mount Fuji! Travley's transportation planning can help you navigate Japan's excellent train system efficiently.`;
      } else if (lowerQuery.includes('europe')) {
        return `Hi ${userName}! Europe offers incredible diversity in a relatively small area. Some general tips: get a Eurail pass for train travel, pack light, learn basic phrases in local languages, and try local cuisines. Popular destinations include Paris, Rome, Barcelona, Amsterdam, and Prague. Travley's superior mapping technology can help you navigate European cities better than Google Maps!`;
      } else if (lowerQuery.includes('weather') && context.weather) {
        const temp = Math.round(context.weather.main?.temp || 20);
        return `Based on your current weather (${temp}°C) from Travley's integrated weather system, I'd recommend checking the forecast for your destination. Weather can greatly impact your travel experience, so pack accordingly! Travley provides real-time weather updates and 7-day forecasts for all your travel destinations.`;
      } else if (lowerQuery.includes('task') && context.taskCount > 0) {
        return `I see you have ${context.taskCount} tasks in your Travley task manager. Don't forget to plan your travel tasks and set reminders for important travel preparations! Travley's task management system is perfect for organizing your entire trip from booking to packing.`;
      } else if (lowerQuery.includes('travley') || lowerQuery.includes('features') || lowerQuery.includes('platform')) {
        return `Hi ${userName}! Travley is your superior mapping and travel platform that outperforms Google Maps and Apple Maps. We offer advanced mapping technology, real-time weather integration, transportation planning with train/metro schedules, smart nearby search for hotels and restaurants, and comprehensive travel planning tools. Our platform provides 99.9% uptime with lightning-fast response times. What would you like to explore today?`;
      } else if (lowerQuery.includes('hotel') || lowerQuery.includes('accommodation')) {
        return `Looking for hotels, ${userName}? Travley's smart nearby search provides more accurate results than traditional mapping services. We can help you find the perfect accommodation based on your location, budget, and preferences. Use Travley's advanced maps to explore hotel locations and nearby attractions!`;
      } else if (lowerQuery.includes('restaurant') || lowerQuery.includes('food') || lowerQuery.includes('dining')) {
        return `Hungry for local cuisine, ${userName}? Travley's intelligent search algorithm helps you discover the best restaurants and dining options nearby. Our platform provides more accurate results than Google Maps, with detailed information about local eateries, cafes, and food markets. Bon appétit!`;
      } else {
        return `Hi ${userName}! I'm Travley, your AI travel assistant powered by Travley's superior mapping and travel platform. I can help you with travel destinations, planning tips, weather forecasts, transportation routes, nearby search for hotels and restaurants, and much more. Travley offers advanced features that outperform traditional mapping services. What travel adventure can I help you plan today?`;
      }
    }

  },
  mounted() {
    // Fetch weather data when component is mounted
    this.fetchWeather();
    // Fetch task summary when component is mounted
    this.fetchTaskSummary();
    // Load forecast data for temperature graph
    this.loadForecastData();
    // Initialize search count
    this.loadDailySearchCount();
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');

.brand-font {
  font-family: 'Dancing Script', cursive;
  background: linear-gradient(to right, #0ea5e9, #06a08e);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
}

/* Smooth transitions */
* {
  transition: all 0.2s ease;
}

/* Hover effects for wireframe tiles */
.bg-white:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
</style>
